"use client";

import { useKeenSlider } from "keen-slider/react";
import "keen-slider/keen-slider.min.css";

import Lightbox from "yet-another-react-lightbox";
import "yet-another-react-lightbox/styles.css";

import React, { useCallback, useMemo, useState } from "react";
import type { SlideImage } from "yet-another-react-lightbox";
import { Heading } from "@repo/ui/heading";
import { CmsMedia, GalleryProps } from "./types";
import { absoluteCmsUrl, BaseImage } from "../base-image/BaseImage";
import Link from "next/link";

function toSlide(media: CmsMedia): SlideImage {
  const lg = media.formats?.large?.url;
  const md = media.formats?.medium?.url;
  const sm = media.formats?.small?.url;
  const src = absoluteCmsUrl(media.url || lg || md || sm);
  const srcSet = [
    media.url && { src: absoluteCmsUrl(media.url), width: media.width },
    media.formats?.large?.url && {
      src: absoluteCmsUrl(media.formats.large.url),
      width: media.formats.large.width,
    },
    media.formats?.medium?.url && {
      src: absoluteCmsUrl(media.formats.medium.url),
      width: media.formats.medium.width,
    },
    media.formats?.small?.url && {
      src: absoluteCmsUrl(media.formats.small.url),
      width: media.formats.small.width,
    },
  ].filter(Boolean) as { src: string; width: number }[];
  return {
    src,
    alt: media.alternativeText ?? media.caption ?? media.name,
    width: media.width,
    height: media.height,
    srcSet,
  } as SlideImage;
}

export default function GallerySlider({ galleries }: GalleryProps) {
  const [open, setOpen] = useState(false);
  const [selectedGallery, setSelectedGallery] = useState<SlideImage[]>([]);

  const [sliderRef] = useKeenSlider<HTMLDivElement>({
    mode: "snap",
    slides: { perView: 1.2, spacing: 16 },
    breakpoints: {
      "(min-width: 768px)": { slides: { perView: 2.2, spacing: 24 } },
      "(min-width: 1024px)": { slides: { perView: 1.7, spacing: 24 } },
    },
  });

  const prepared = useMemo(() => {
    return galleries.map((g) => {
      const slides = (g.images || []).map((img) => toSlide(img));
      const preview = g.previewImage ? toSlide(g.previewImage) : slides[0];
      return {
        id: g.id,
        title: g.title ?? "Gallery",
        slides,
        preview,
      };
    });
  }, [galleries]);

  const handleOpen = useCallback(
    (idx: number) => {
      setSelectedGallery(prepared[idx]?.slides ?? []);
      setOpen(true);
    },
    [prepared],
  );

  return (
    <>
      <div className="max-w-7xl mx-auto w-full" id="gallery">
        <div className="max-w-2xl reveal-stagger flex px-4 flex-col gap-6 md:py-12 py-12 pb-0 sm:mt-12">
          <Heading
            level="h2"
            className="xl:text-6xl md:text-5xl text-4xl font-normal tracking-tighter reveal-item"
            title="Our Gallery"
          />
          <div className="text-grey-900 md:text-lg text-base max-w-lg reveal-item">
            <p>
              Explore our gallery and discover the refined elegance of La Maison
              Suisse Hotels. From modern interiors to thoughtfully designed
              spaces, each image reflects the style, comfort, and unique Swiss
              charm that define your stay.
            </p>
          </div>
          <Link
            className="group relative z-10 h-fit py-4 px-6 flex items-center gap-2 font-medium w-fit bg-black rounded-4xl text-white reveal-item"
            href="/gallery"
            prefetch={false}
            aria-label="See more photos in the hotel gallery"
          >
            <span className="w-full h-full opacity-0 flex border-black border-2 absolute left-0 top-0 -z-10 rounded-4xl transition group-focus:scale-x-104 group-focus:scale-y-110 group-hover:scale-x-105 group-hover:scale-y-115 group-hover:opacity-100"></span>
            See More Photos
          </Link>
        </div>
      </div>
      <div className="reveal-item w-full max-w-screen-2xl mx-auto">
        <div
          ref={sliderRef}
          className="keen-slider px-4 md:py-8 pb-8"
          role="list"
          aria-label="Galleries"
        >
          {prepared.map((g, index) => (
            <div key={g.id} className="keen-slider__slide" role="listitem">
              <div className="min-w-[300px] relative">
                <button
                  type="button"
                  className="relative h-[600px] w-full overflow-hidden cursor-pointer text-left"
                  onClick={() => handleOpen(index)}
                  aria-label={`Open ${g.title}`}
                >
                  <BaseImage
                    src={g.preview?.src || ""}
                    alt={g.preview?.alt || g.title || "Gallery preview"}
                    fill
                    sizes="(max-width: 768px) 90vw, (max-width: 1024px) 60vw, 50vw"
                    loading="lazy"
                    className="object-cover"
                    quality={90}
                  />
                  <p className="mt-2 text-white capitalize absolute bottom-4 left-4 text-lg">
                    {g.title}
                  </p>
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <Lightbox
        open={open}
        close={() => setOpen(false)}
        slides={selectedGallery}
      />
    </>
  );
}
